import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TasksList from './TasksList';
import ViewTask from './ViewTask';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './Tasks.css';

function Tasks({ initialTask, returnToPO: returnToProp, onReturnConsumed }) {
  const navigate = useNavigate();
  const { t } = useTranslation('production');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'view', 'edit', 'create'

  // Open the task requested through /tasks/:id (also when this tab is already open)
  useEffect(() => {
    if (initialTask?.id) {
      setSelectedTaskId(parseInt(initialTask.id, 10));
      setViewMode('view');
    }
  }, [initialTask?.nonce]);

  // The origin (production order or dashboard) only applies to the task it was opened for
  const returnToPO = returnToProp
    && (returnToProp.targetId == null || String(returnToProp.targetId) === String(selectedTaskId))
    ? returnToProp : null;

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: t('tasks.toolbar.titles.tasks'),
          buttons: [
            {
              label: t('tasks.toolbar.buttons.newTask'),
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: t('tasks.toolbar.buttons.refresh'),
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: returnToPO ? `${returnToPO.label}` : t('tasks.toolbar.titles.viewTask'),
          showBack: true,
          onBack: handleBack,
          backLabel: returnToPO ? `← ${returnToPO.label}` : undefined,
          buttons: [
            {
              label: t('tasks.toolbar.buttons.edit'),
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: t('tasks.toolbar.buttons.complete'),
              icon: '✅',
              onClick: handleComplete,
              variant: 'success'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: t('tasks.toolbar.titles.editTask'),
          showBack: false,
          buttons: [
            {
              label: t('tasks.toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('tasks.toolbar.buttons.save'),
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: t('tasks.toolbar.titles.newTask'),
          showBack: false,
          buttons: [
            {
              label: t('tasks.toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('tasks.toolbar.buttons.create'),
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode, t, returnToPO]);

  const handleSelectTask = (taskId) => {
    setSelectedTaskId(taskId);
    setViewMode('view');
  };

  const handleNew = () => {
    setSelectedTaskId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleComplete = () => {
    if (window.viewTaskRef && window.viewTaskRef.handleComplete) {
      window.viewTaskRef.handleComplete();
    }
  };

  const handleSave = () => {
    if (window.viewTaskRef && window.viewTaskRef.handleSave) {
      window.viewTaskRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedTaskId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    const originPO = returnToPO && (returnToPO.orderId || (returnToPO.page === 'production-order' ? returnToPO.detailId : null));
    if (returnToPO?.path) {
      if (onReturnConsumed) onReturnConsumed();
      navigate(returnToPO.path);
    } else if (originPO) {
      if (onReturnConsumed) onReturnConsumed();
      navigate(`/production-orders/${originPO}`, { state: { innerTab: 'tasks' } });
    } else {
      setViewMode('list');
      setSelectedTaskId(null);
    }
  };

  const handleRefresh = () => {
    if (window.tasksListRef && window.tasksListRef.fetchTasks) {
      window.tasksListRef.fetchTasks();
    }
  };

  const handleTaskSaved = (task) => {
    setSelectedTaskId(task.task_id);
    setViewMode('view');
  };

  return (
    <div className="tasks-page">
      <ResizablePanels
        leftPanel={
          <TasksList
            ref={ref => window.tasksListRef = ref}
            onSelectTask={handleSelectTask}
            selectedTaskId={selectedTaskId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewTask
              ref={ref => window.viewTaskRef = ref}
              taskId={selectedTaskId}
              onBack={handleBack}
              backLabel={returnToPO ? returnToPO.label : undefined}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onSave={handleTaskSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={25}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default Tasks;